import React, { useEffect, useRef, useState } from 'react';
import { DefaultButton, Spinner, Stack, Text } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import { idpService } from 'services/api/identity-provider.service';
import { COLIBO_CALLBACK_MESSAGE } from 'features/Rooms/PersonalDashboard/ColiboWidget/colibo.helpers';

type CallbackStatus = 'connecting' | 'no-opener' | 'error';

/** How long the popup waits for the widget to redeem the code and close it. */
const HANDOFF_TIMEOUT_MS = 30_000;

// Colibo redirects the connect popup here. Normally the popup only hands code and state back to the widget
// that opened it: inside OX the campus is a cross-site iframe whose storage is partitioned, so this top-level
// window may hold another session's token (or none). The widget redeems the code and closes this window.
// Without an opener the popup redeems the code itself (works when the campus is the top-level page).
// Nothing from the URL is rendered; only fixed strings are shown.
const ColiboAuthCallback: React.FC = () => {
    const [status, setStatus] = useState<CallbackStatus>('connecting');
    // An authorization code can be redeemed only once; StrictMode runs effects twice in dev.
    const hasHandledCode = useRef(false);

    useEffect(() => {
        if (hasHandledCode.current) return;
        hasHandledCode.current = true;

        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        // Drop code/state from the address bar and history right away.
        window.history.replaceState(null, '', window.location.pathname);

        if (params.get('error') || !code || !state) {
            setStatus('error');
            return;
        }

        if (window.opener && !window.opener.closed) {
            // Same-origin target only: code and state must never reach another origin.
            window.opener.postMessage({ type: COLIBO_CALLBACK_MESSAGE, code, state }, window.location.origin);
            // Still open after this: the widget is gone (the user left the dashboard) or failed to close us.
            window.setTimeout(() => setStatus('error'), HANDOFF_TIMEOUT_MS);
            return;
        }

        idpService
            .connectColibo(code, state)
            // The widget re-checks the connection status when the popup closes.
            .then(() => setStatus('no-opener'))
            .catch(() => setStatus('error'));
    }, []);

    const messages: Record<CallbackStatus, string> = {
        connecting: IvicosStrings.coliboCallbackConnecting,
        'no-opener': IvicosStrings.coliboCallbackNoOpener,
        error: IvicosStrings.coliboCallbackError
    };

    return (
        <Stack horizontalAlign="center" verticalAlign="center" tokens={{ childrenGap: 16 }} styles={{ root: { minHeight: '100vh', padding: 24 } }}>
            {status === 'connecting' && <Spinner />}
            <Text variant="large" style={{ textAlign: 'center' }} data-test-id="colibo-callback-message">
                {messages[status]}
            </Text>
            {status !== 'connecting' && <DefaultButton text={IvicosStrings.coliboCallbackClose} onClick={() => window.close()} />}
        </Stack>
    );
};

export default ColiboAuthCallback;
